// Webapp Test Status helpers
// --------------------------
// Per-webapp predicates derived from the test history + aggregate.
// Used by surfaces that need to decide *whether to show stuff* about
// a webapp — pill states, count contributions, badge gating — based
// on whether the app has any meaningful test history yet.
//
// Centralised here so the Dashboard ApplicationCard and the
// Applications row read the same definition. Inlining the logic in
// each page was the cause of an earlier drift bug (the two pages
// drew the same app with different pill colors because their local
// versions evaluated differently for the same input).

import WebApplicationClass from "@/models/webApplication";
import { NO_TESTS_PHASE_VALUE } from "@/constants/definitions";
import { INCOMPLETE_END_TRIGGERS } from "@/utils/testStatusLabel";

/**
 * Has this app had at least one assessment that produced a meaningful
 * result? Used to decide whether the app's stored aggregate counts
 * (webAppTestStatus.opencrits etc.) should be surfaced.
 *
 * Errs on the strict side: a failed ServerUnreachable / Cancelled /
 * InfrastructureFailure test does NOT count, because those don't
 * represent a real assessment of the app. A test must either
 * (a) have an endTime AND a clean endTrigger, or (b) be reflected in
 * the BE aggregate (lastTestDate populated, phase not No_Tests).
 */
export function hasAppBeenTested(app: WebApplicationClass): boolean {
  const status = app.webAppTestStatus;
  const phase = status?.currentPhase || "";
  const completedTests =
    app.tests?.filter(
      (testEntry) =>
        testEntry.endTime &&
        !INCOMPLETE_END_TRIGGERS.has(testEntry.endTrigger ?? ""),
    ) ?? [];
  const hasLastTest =
    !!status?.lastTestDate && status.lastTestDate.getTime() > 0;
  return (
    (completedTests.length > 0 || hasLastTest) &&
    phase !== NO_TESTS_PHASE_VALUE &&
    phase !== ""
  );
}
